import { TFunction } from 'i18next';
import { DrawerContainer } from '../../../../components/Drawer/DrawerContainer';
import { Visita } from '../../consts/visitasTypes';
import VisitDetails from '../VisitaDetails';
import { AssignPreventorDrawer } from './AssignPreventorDrawer';
import { CancelVisitDrawer } from './CancelVisitDrawer';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';
import { SendVisitToRevisionDrawer } from './SendVisitToRevisionDrawer';
import { ProgramVisitDrawer } from './programVisitDrawer';
import { SeeCancelMotiveDrawer } from './SeeCancelMotiveDrawer';
import { CancelVisitsDrawer } from './CancelVisitsDrawer';
import { VisitHistoryDrawer } from './VisitHistoryDrawer';

export const DRAWER_ACTION_VALUES = {
    ASSIGN_PREVENTOR: 'assign-preventor',
    ASSIGN_PREVENTOR_MASSIVE: 'assign-preventor-massive',
    REASSIGN_PREVENTOR: 'reassign-preventor',
    REASSIGN_PREVENTOR_MASSIVE: 'reassign-preventor-massive',
    SEE_DETAIL: 'see-detail',
    CANCEL_VISIT: 'cancel-visit',
    CANCEL_VISITS_MASSIVE: 'cancel-visits-massive',
    REJECT_VISIT: 'reject-visit',
    ESTABLISHMENT: 'establishment',
    SEND_VISIT_TO_REVISION: 'send-visit-to-revision',
    PROGRAM_VISIT: 'program-visit',
    REQUEST_CHANGES: 'request-changes',
    SEE_CANCEL_MOTIVE: 'see-cancel-motive',
    SEE_VISIT_HISTORY: 'see-visit-history'
} as const;

export type DrawerAction = (typeof DRAWER_ACTION_VALUES)[keyof typeof DRAWER_ACTION_VALUES];

interface DrawerManagerProps {
    open: boolean;
    actionType: DrawerAction | null;
    setOpen: (value: boolean) => void;
    closeDrawer: () => void;
    onSuccess?: () => void;
    selected: Visita | null;
    translate: TFunction;
    selectedVisitsIds: number[];
    selectedVisitsSummary?: { id: number; label: string; subLabel?: string }[];
    clearVisitsSelection: () => void;
}

const DrawerManager = ({
    open,
    actionType,
    setOpen,
    closeDrawer,
    onSuccess,
    selected,
    translate,
    selectedVisitsIds,
    selectedVisitsSummary = [],
    clearVisitsSelection
}: DrawerManagerProps) => {
    const DrawerInputManager = () => {
        switch (actionType) {
            case DRAWER_ACTION_VALUES.ASSIGN_PREVENTOR:
                return (
                    <AssignPreventorDrawer
                        selected={selected}
                        closeDrawer={closeDrawer}
                        onSuccess={onSuccess}
                        translate={translate}
                        clearVisitsSelection={clearVisitsSelection}
                    />
                );
            case DRAWER_ACTION_VALUES.ASSIGN_PREVENTOR_MASSIVE:
                return (
                    <AssignPreventorDrawer
                        selected={selected}
                        closeDrawer={closeDrawer}
                        onSuccess={onSuccess}
                        translate={translate}
                        isMassiveAssignation={true}
                        selectedVisitsIds={selectedVisitsIds}
                        clearVisitsSelection={clearVisitsSelection}
                    />
                );
            case DRAWER_ACTION_VALUES.REASSIGN_PREVENTOR:
                return (
                    <AssignPreventorDrawer
                        selected={selected}
                        closeDrawer={closeDrawer}
                        onSuccess={onSuccess}
                        translate={translate}
                        selectedVisitsIds={selectedVisitsIds}
                        isReassignation={true}
                        clearVisitsSelection={clearVisitsSelection}
                    />
                );
            case DRAWER_ACTION_VALUES.REASSIGN_PREVENTOR_MASSIVE:
                return (
                    <AssignPreventorDrawer
                        selected={selected}
                        closeDrawer={closeDrawer}
                        onSuccess={onSuccess}
                        translate={translate}
                        isMassiveAssignation={true}
                        selectedVisitsIds={selectedVisitsIds}
                        isReassignation={true}
                        clearVisitsSelection={clearVisitsSelection}
                    />
                );

            case DRAWER_ACTION_VALUES.SEE_DETAIL:
                return selected ? (
                    <>
                        <DrawerHeader
                            onIconClick={() => setOpen(false)}
                            text={translate(`drawer.title.${actionType?.toLowerCase()}`)}
                        />
                        <VisitDetails visita={selected} />
                    </>
                ) : (
                    <></>
                );

            case DRAWER_ACTION_VALUES.CANCEL_VISIT:
                return (
                    <CancelVisitDrawer
                        translate={translate}
                        selected={selected}
                        setOpen={setOpen}
                        onSuccess={onSuccess}
                        actionType={actionType}
                    />
                );

            case DRAWER_ACTION_VALUES.CANCEL_VISITS_MASSIVE:
                return (
                    <CancelVisitsDrawer
                        translate={translate}
                        setOpen={setOpen}
                        selectedVisitsIds={selectedVisitsIds}
                        selectedVisitsSummary={selectedVisitsSummary}
                        clearVisitsSelection={clearVisitsSelection}
                    />
                );

            case DRAWER_ACTION_VALUES.REJECT_VISIT:
                return (
                    <CancelVisitDrawer
                        translate={translate}
                        selected={selected}
                        setOpen={setOpen}
                        onSuccess={onSuccess}
                        actionType={actionType}
                    />
                );
            case DRAWER_ACTION_VALUES.PROGRAM_VISIT:
                return (
                    <ProgramVisitDrawer
                        translate={translate}
                        selected={selected}
                        setOpen={setOpen}
                        selectedVisitsIds={selectedVisitsIds}
                        selectedVisitsSummary={selectedVisitsSummary}
                        clearVisitsSelection={clearVisitsSelection}
                    />
                );

            case DRAWER_ACTION_VALUES.ESTABLISHMENT:
                return (
                    <CancelVisitDrawer
                        translate={translate}
                        selected={selected}
                        setOpen={setOpen}
                        actionType={actionType}
                    />
                );

            case DRAWER_ACTION_VALUES.SEND_VISIT_TO_REVISION:
                return (
                    <SendVisitToRevisionDrawer
                        translate={translate}
                        selected={selected}
                        setOpen={setOpen}
                        onSuccess={onSuccess}
                        actionType={actionType}
                    />
                );
            case DRAWER_ACTION_VALUES.SEE_CANCEL_MOTIVE:
                return (
                    <SeeCancelMotiveDrawer
                        translate={translate}
                        selected={selected}
                        setOpen={setOpen}
                        actionType={actionType}
                    />
                );
            case DRAWER_ACTION_VALUES.SEE_VISIT_HISTORY:
                return (
                    <VisitHistoryDrawer
                        translate={translate}
                        selected={selected}
                        closeDrawer={closeDrawer}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <DrawerContainer setOpen={setOpen} open={!!open} customWidth={700}>
            <DrawerInputManager />
        </DrawerContainer>
    );
};

export default DrawerManager;
